/**
 * ServiceSettingsSheet — per-service controls for the owner.
 * Toggles WhatsApp / Phone (Premium-gated), and quick actions.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, MessageCircle, Phone, Pencil, EyeOff, Eye, Trash2, Sparkles, Lock } from "lucide-react";
import { updateService, deleteService } from "@/lib/entitiesStore";

const ServiceSettingsSheet = ({ open, onClose, service, isPremium }) => {
    const navigate = useNavigate();
    const [waOn, setWaOn] = useState(!!service?.whatsappEnabled);
    const [phOn, setPhOn] = useState(!!service?.phoneEnabled);

    if (!open || !service) return null;

    const toggleWa = () => {
        if (!isPremium) return;
        const next = !waOn;
        setWaOn(next);
        updateService(service.id, { whatsappEnabled: next });
    };
    const togglePh = () => {
        if (!isPremium) return;
        const next = !phOn;
        setPhOn(next);
        updateService(service.id, { phoneEnabled: next });
    };

    const close = () => onClose?.();

    return (
        <div className="absolute inset-0 z-50 flex items-end" data-testid="service-settings-sheet" role="dialog" aria-modal="true">
            <button
                onClick={close}
                aria-label="Close"
                className="absolute inset-0 bg-black/55 animate-fade-in"
                data-testid="service-settings-scrim"
            />
            <div className="relative z-10 w-full bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.4)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]">
                <div className="flex justify-center pt-2.5 pb-1">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>
                <div className="px-5 pt-1.5 pb-2.5 flex items-center justify-between border-b border-border/60">
                    <div className="min-w-0">
                        <h2 className="font-display text-[16px] font-semibold truncate">{service.name}</h2>
                        <p className="text-[11.5px] text-muted-foreground">Service settings</p>
                    </div>
                    <button onClick={close} className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted" aria-label="Close" data-testid="service-settings-close">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 pt-4 space-y-3 max-h-[70vh] overflow-y-auto no-scrollbar">
                    {/* Premium-gated toggles */}
                    <div>
                        <div className="flex items-center justify-between mb-2 px-1">
                            <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Contact toggles</span>
                            {!isPremium && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                                    <Sparkles size={9} /> Premium
                                </span>
                            )}
                        </div>
                        <ToggleRow
                            testId="svc-settings-toggle-whatsapp"
                            icon={MessageCircle}
                            title="Enable WhatsApp"
                            caption={isPremium ? "Show a WhatsApp button on this service" : "Upgrade to Premium to enable"}
                            checked={isPremium && waOn}
                            disabled={!isPremium}
                            locked={!isPremium}
                            onChange={toggleWa}
                        />
                        <div className="h-2" />
                        <ToggleRow
                            testId="svc-settings-toggle-phone"
                            icon={Phone}
                            title="Show phone number"
                            caption={isPremium ? "Allow clients to see and call your number" : "Upgrade to Premium to enable"}
                            checked={isPremium && phOn}
                            disabled={!isPremium}
                            locked={!isPremium}
                            onChange={togglePh}
                        />
                        {!isPremium && (
                            <button
                                type="button"
                                data-testid="svc-settings-upgrade"
                                onClick={() => { close(); navigate("/premium"); }}
                                className="mt-2 ml-1 text-[12.5px] font-medium text-primary hover:underline"
                            >
                                Upgrade to Premium →
                            </button>
                        )}
                    </div>

                    <hr className="border-border/60" />

                    {/* Actions */}
                    <ActionRow
                        testId="svc-settings-edit"
                        icon={Pencil}
                        label="Edit service"
                        onClick={() => { close(); navigate(`/services/${service.id}`); }}
                    />
                    <ActionRow
                        testId="svc-settings-hide"
                        icon={service.hidden ? Eye : EyeOff}
                        label={service.hidden ? "Unhide service" : "Hide service"}
                        onClick={() => updateService(service.id, { hidden: !service.hidden })}
                    />
                    <ActionRow
                        testId="svc-settings-delete"
                        icon={Trash2}
                        label="Delete service"
                        danger
                        onClick={() => {
                            if (window.confirm(`Delete "${service.name}"?`)) {
                                deleteService(service.id);
                                close();
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const ToggleRow = ({ icon: Icon, title, caption, checked, disabled, locked, onChange, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={() => !disabled && onChange?.()}
        disabled={disabled}
        className={[
            "w-full flex items-center gap-3 p-3.5 rounded-2xl border bg-secondary text-left transition-colors border-border",
            disabled ? "opacity-70 cursor-not-allowed" : "hover:bg-muted",
        ].join(" ")}
    >
        <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={16} />
        </span>
        <span className="flex-1 min-w-0">
            <span className="flex items-center gap-1.5">
                <span className="text-[14px] font-medium leading-tight">{title}</span>
                {locked && <Lock size={11} className="text-muted-foreground" />}
            </span>
            <span className="block text-[11.5px] text-muted-foreground mt-0.5 truncate">{caption}</span>
        </span>
        <span
            className={["relative h-6 w-10 rounded-full shrink-0 transition-colors", checked ? "bg-primary" : "bg-muted"].join(" ")}
            aria-hidden
        >
            <span
                className={["absolute top-0.5 h-5 w-5 rounded-full bg-background shadow transition-all", checked ? "left-[18px]" : "left-0.5"].join(" ")}
            />
        </span>
    </button>
);

const ActionRow = ({ icon: Icon, label, onClick, danger, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={[
            "w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card hover:bg-accent/40 active:scale-[0.99] transition-[transform,background-color]",
            danger ? "text-destructive" : "",
        ].join(" ")}
    >
        <span className={[
            "h-9 w-9 rounded-xl flex items-center justify-center",
            danger ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
        ].join(" ")}>
            <Icon size={16} />
        </span>
        <span className="text-[13.5px] font-medium">{label}</span>
    </button>
);

export default ServiceSettingsSheet;
